import { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import socket from 'websockets/socket';
import moment from 'moment-timezone';
import { v4 as uuidV4 } from 'uuid';
import { uiActions } from 'careand-redux/slices/ui/ui';
import type { ChatSavedPromptRequest, PromptRequest, AssistantMessage } from '../generated/api';
import {
  type ExtraArgs,
  type PromptOutput,
  type PromptInput,
  type PromptName,
  runSavedPromptTyped as runSavedPromptTypedThunk,
} from '../thunks/runSavedPromptTyped';
import { type CareandPrompts } from '../generated/prompts';

function getStreamingKey(id?: string) {
  return `assistant-${id}`;
}

type StreamingData = {
  index: number;
  text: string;
  streamingKey?: string;
};

type Streams = Record<string, StreamingData[]>;

const EMPTY_STREAM: StreamingData[] = [];
const DEFAULT_ARRAY: string[] = [];
// How often to look for the socket object when the redux connection status is
// already 'connected' but Socket.open() has not run yet.
const SOCKET_RETRY_MS = 500;

// Message arrays are always replaced wholesale, never mutated in place, so an
// element-wise identity check is enough to tell two versions apart.
function sameMessages(a?: AssistantMessage[], b?: AssistantMessage[]) {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  return a.every((message, index) => message === b[index]);
}

function useAssistant(id?: string, manageReduxState?: boolean, suggestReplies?: boolean) {
  const dispatch = useAppDispatch();
  const socketConnection = useAppSelector((state) => state.resource.socketConnection);

  const reduxAssistant = useAppSelector((state) => state.ui.assistants.find((it) => it.id === id));

  const initialMessages = useMemo(() => {
    return reduxAssistant?.messages ?? [];
  }, [reduxAssistant]);

  const defaultStreamingKey = useMemo(() => getStreamingKey(id), [id]);

  // Per-call streaming buffers, keyed by streamingKey. Concurrent calls each
  // accumulate into their own slot so they can't trample each other.
  const [streams, setStreams] = useState<Streams>(() => ({ [defaultStreamingKey]: [] }));
  // Keys whose API call is still in flight.
  const [loadingSet, setLoadingSet] = useState<Set<string>>(() => new Set());
  // Most recent call without a custom streamingKey. streamingText/loadingReply
  // expose this slot — callers that pass their own key run in isolation.
  const [primaryKey, setPrimaryKey] = useState<string>(defaultStreamingKey);
  // Token of the call that currently owns each buffer. A call only tears its
  // buffer down while it still owns it, so a stopped or superseded request
  // can't end the request that replaced it on the same key.
  const streamOwners = useRef<Record<string, string>>({});
  // Keys that took the primary slot, oldest first. When the newest call ends
  // the slot goes back to the previous one instead of pointing at a deleted
  // buffer while an older call is still streaming.
  const primaryKeys = useRef<string[]>([]);

  const [localMessages, setLocalMessages] = useState<AssistantMessage[]>(initialMessages);
  // Synchronous mirror of localMessages: two sends in the same tick have to
  // build their payload from the already-appended list, not a stale closure.
  const localMessagesRef = useRef<AssistantMessage[]>(initialMessages);
  // Last value written to redux, used to tell our own pending change apart
  // from an update that landed in the store from somewhere else.
  const pushedMessages = useRef<AssistantMessage[]>(initialMessages);
  const syncedId = useRef(id);
  const expectedRequest = useRef<string | undefined>();
  const [replySuggestions, setSuggestions] = useState<string[]>(DEFAULT_ARRAY);
  const suggestionKey = useRef<string | undefined>();
  const [loadingReplySuggestions, setLoadingReplySuggestions] = useState(false);

  const subject = reduxAssistant?.subject;

  const updateLocalMessages = useCallback(
    (next: AssistantMessage[] | ((prev: AssistantMessage[]) => AssistantMessage[])) => {
      const value = typeof next === 'function' ? next(localMessagesRef.current) : next;
      localMessagesRef.current = value;
      setLocalMessages(value);
    },
    [],
  );

  if (syncedId.current !== id) {
    // Switched conversation: adopt the new one's history instead of writing
    // the previous conversation's messages onto it.
    syncedId.current = id;
    localMessagesRef.current = initialMessages;
    pushedMessages.current = initialMessages;
    setLocalMessages(initialMessages);
  } else if (
    manageReduxState &&
    reduxAssistant &&
    sameMessages(localMessages, pushedMessages.current) &&
    !sameMessages(initialMessages, pushedMessages.current)
  ) {
    // The store moved underneath us (e.g. saved conversations finished
    // loading) and we have no pending local change — adopt it, otherwise the
    // next write-back would clobber it with our stale copy.
    localMessagesRef.current = initialMessages;
    pushedMessages.current = initialMessages;
    setLocalMessages(initialMessages);
  }

  useEffect(() => {
    setPrimaryKey(defaultStreamingKey);
    primaryKeys.current = [];
    setStreams((prev) =>
      defaultStreamingKey in prev ? prev : { ...prev, [defaultStreamingKey]: [] },
    );
  }, [defaultStreamingKey]);

  const loadingReply = loadingSet.has(primaryKey);

  useEffect(() => {
    if (manageReduxState) {
      pushedMessages.current = localMessages;
      dispatch(uiActions.updateAssistant({ id, messages: localMessages }));
    }
  }, [dispatch, id, manageReduxState, localMessages]);

  useEffect(() => {
    if (manageReduxState) {
      dispatch(uiActions.updateAssistant({ id, loading: loadingReply }));
    }
  }, [dispatch, id, manageReduxState, loadingReply]);

  useEffect(() => {
    if (manageReduxState && !subject && localMessages.length === 1) {
      generateChatSubject(localMessages[0]!.content).then((subject) => {
        if (subject) {
          dispatch(uiActions.updateAssistant({ id, subject }));
        }
      });
    }
  }, [dispatch, id, manageReduxState, localMessages, subject]);

  const messages = useMemo(() => {
    return manageReduxState ? reduxAssistant?.messages ?? [] : localMessages;
  }, [manageReduxState, reduxAssistant, localMessages]);

  useEffect(() => {
    const eventName = 'ai-streaming';

    const listener = (data: StreamingData) => {
      const streamKey = data.streamingKey;
      if (!streamKey) return;
      setStreams((prev) => {
        if (!(streamKey in prev)) return prev;
        const existing = prev[streamKey] ?? EMPTY_STREAM;
        const base = data.index === 0 ? EMPTY_STREAM : existing;
        return { ...prev, [streamKey]: [...base, data] };
      });
    };

    if (socketConnection !== 'connected') return;

    let instance = socket.getSocket();
    if (instance) {
      instance.on(eventName, listener);
      return () => instance.removeListener(eventName, listener);
    }

    // The redux status can lead the socket object (close(false) drops it
    // without dispatching a status change), and nothing else re-runs this
    // effect once the socket is recreated — poll until it shows up, otherwise
    // no streamed text is ever rendered.
    const retry = setInterval(() => {
      instance = socket.getSocket();
      if (!instance) return;
      clearInterval(retry);
      instance.on(eventName, listener);
    }, SOCKET_RETRY_MS);

    return () => {
      clearInterval(retry);
      if (instance) instance.removeListener(eventName, listener);
    };
  }, [socketConnection]);

  const beginStream = useCallback((key: string, makePrimary: boolean) => {
    const token = uuidV4();
    streamOwners.current[key] = token;
    setStreams((prev) => ({ ...prev, [key]: [] }));
    setLoadingSet((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    if (makePrimary) {
      primaryKeys.current = [...primaryKeys.current.filter((it) => it !== key), key];
      setPrimaryKey(key);
    }
    return token;
  }, []);

  const endStream = useCallback(
    (key: string, token: string) => {
      // A stopped or superseded call no longer owns the key: tearing it down
      // here would kill the request that took its place.
      if (streamOwners.current[key] !== token) return;
      delete streamOwners.current[key];
      primaryKeys.current = primaryKeys.current.filter((it) => it !== key);
      setLoadingSet((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      setStreams((prev) => {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setPrimaryKey((prev) =>
        prev === key
          ? primaryKeys.current[primaryKeys.current.length - 1] ?? defaultStreamingKey
          : prev,
      );
    },
    [defaultStreamingKey],
  );

  const regenerateSuggestions = useCallback(() => {
    const key = uuidV4();
    suggestionKey.current = key;
    const chatStr = messages.map((it) => `${it.role}: ${it.content}`).join('\n\n');

    setLoadingReplySuggestions(true);
    runSavedPromptTypedThunk('Chat Reply Suggestions', { chat: chatStr }, {})
      .then((result) => {
        if (suggestionKey.current !== key) return;
        // A fresh empty array would change the state identity without passing
        // the length guard below, re-triggering this request forever.
        setSuggestions(result?.length ? result : DEFAULT_ARRAY);
      })
      .catch((err) => {
        /*ignore*/
      })
      .finally(() => {
        if (suggestionKey.current === key) setLoadingReplySuggestions(false);
      });
  }, [messages]);

  useEffect(() => {
    const message = messages[messages.length - 1];
    if (!message || !suggestReplies || replySuggestions?.length > 0) {
      return;
    }

    if (message.role === 'assistant') {
      regenerateSuggestions();
    } else {
      setSuggestions(DEFAULT_ARRAY);
    }
  }, [messages, suggestReplies, regenerateSuggestions, replySuggestions]);

  const clearMessages = useCallback(() => {
    updateLocalMessages([]);
  }, [updateLocalMessages]);

  const stopRequest = useCallback(() => {
    expectedRequest.current = undefined;
    streamOwners.current = {};
    primaryKeys.current = [];
    suggestionKey.current = undefined;
    setLoadingSet(new Set());
    setStreams({ [defaultStreamingKey]: [] });
    setPrimaryKey(defaultStreamingKey);
    setLoadingReplySuggestions(false);
  }, [defaultStreamingKey]);

  const prompt2 = useCallback(
    async (
      aiPrompt: PromptRequest,
      patient_id?: string,
      fields?: PromptRequest['fields'],
      document_id?: string,
      budget_tokens?: number,
    ) => {
      const token = beginStream(defaultStreamingKey, true);
      try {
        let response = await dispatch(
          api.AI.prompt2({
            promptRequest: {
              name: aiPrompt.name,
              prompt: aiPrompt.prompt,
              context: aiPrompt.context,
              provider: aiPrompt.provider,
              model: aiPrompt.model,
              include_main_context: aiPrompt.include_main_context,
              fields,
              patient_id,
              document_id,
              budget_tokens,
              allowed_tools: aiPrompt.allowed_tools,
            },
            streamingKey: defaultStreamingKey,
          }),
          { errorTitle: 'AI Request' },
        );
        return response?.value;
      } finally {
        endStream(defaultStreamingKey, token);
      }
    },
    [dispatch, defaultStreamingKey, beginStream, endStream],
  );

  const chatSavedPrompt = useCallback(
    async (data: ChatSavedPromptRequest) => {
      let initialDate = moment().unix();
      updateLocalMessages([]);
      const token = beginStream(defaultStreamingKey, true);
      const requestId = uuidV4();
      expectedRequest.current = requestId;
      try {
        let result = await dispatch(
          api.AI.chatSavedPrompt({ ...data, streamingKey: defaultStreamingKey }),
          { errorTitle: 'AI Chat' },
        );
        if (result && expectedRequest.current === requestId) {
          const parsed = result.map((message, index) => {
            message.date = index === result.length - 1 ? moment().unix() : initialDate;
            return message;
          });
          updateLocalMessages(parsed);
        }
        if (expectedRequest.current === requestId) {
          expectedRequest.current = undefined;
        }
        return result;
      } finally {
        endStream(defaultStreamingKey, token);
      }
    },
    [dispatch, defaultStreamingKey, beginStream, endStream, updateLocalMessages],
  );

  const runSavedPromptTyped = useCallback(
    async <T extends PromptName>(
      name: T,
      input: PromptInput<CareandPrompts[T]>,
      extra: ExtraArgs<CareandPrompts[T]> & {
        streamingKey?: string;
        budgetTokens?: number;
      },
    ): Promise<PromptOutput<CareandPrompts[T]> | undefined> => {
      // Auto-generate a key when none is provided so concurrent calls don't
      // share a buffer. Calls with their own key stay out of the primary slot.
      const callKey = extra.streamingKey ?? uuidV4();
      const makePrimary = !extra.streamingKey;

      const token = beginStream(callKey, makePrimary);
      try {
        return await runSavedPromptTypedThunk(name, input, {
          ...extra,
          streamingKey: callKey,
        });
      } finally {
        endStream(callKey, token);
      }
    },
    [beginStream, endStream],
  );

  const chatAssistant = useCallback(
    async (text: string, clear = false) => {
      const message: AssistantMessage = {
        content: text,
        role: 'user',
        date: moment().unix(),
      };

      // Build the payload from the synchronous mirror so a second send in the
      // same tick still sees the first one's message.
      const newMessages = clear ? [message] : [...localMessagesRef.current, message];
      updateLocalMessages(newMessages);

      const token = beginStream(defaultStreamingKey, true);
      const requestId = uuidV4();
      expectedRequest.current = requestId;

      try {
        const result = await dispatch(
          api.AI.chatAssistant({
            streamingKey: defaultStreamingKey,
            assistantConversation: { messages: newMessages },
          }),
          { errorTitle: 'AI Chat' },
        );

        // Check if this request is still the expected one
        if (result && expectedRequest.current === requestId) {
          result.date = moment().unix();
          updateLocalMessages((prevMsgs) => [...prevMsgs, result]);
        }

        if (expectedRequest.current === requestId) {
          expectedRequest.current = undefined;
        }
      } finally {
        endStream(defaultStreamingKey, token);
      }
    },
    [dispatch, defaultStreamingKey, beginStream, endStream, updateLocalMessages],
  );

  const lastReply = useMemo(() => {
    let assistantMessages = messages.filter((message) => message.role === 'assistant');
    return assistantMessages[assistantMessages.length - 1];
  }, [messages]);

  const streamingText = useMemo(() => {
    const data = streams[primaryKey] ?? EMPTY_STREAM;
    return data.map(({ text }) => text).join('');
  }, [streams, primaryKey]);

  const generateChatSubject = useCallback(async (firstMessage: string) => {
    return await runSavedPromptTypedThunk('Chat Subject Generation', { firstMessage }, {});
  }, []);

  return {
    prompt2,
    chatSavedPrompt,
    chatAssistant,
    streamingText,
    lastReply,
    messages,
    loadingReply,
    clearMessages,
    stopRequest,
    runSavedPromptTyped,
    replySuggestions,
    regenerateSuggestions,
    loadingReplySuggestions,
  };
}

export default useAssistant;
